import React from "react";
import {useSelector} from "../../../shared-services/redux-tiny";
import {t} from "../../../shared-services/translate";
import currentTheme from "../../../react-themes/theme";
import useWindowTools from "../../../react-hooks/useWindowTools";
import useImageLoadWatch from "../../shared/useImageLoadWatch";

import {FaLink} from "react-icons/fa";
import PageContentWithCart from "../../layouts/PageContentWithCart";
import {FarmyColumnSet, FarmyColumn} from "../../layouts/components/FarmyColumns";
import {Title1, Title2, Title3} from "../../layouts/components/Titles";
import ComponentPlaceholder from "../../shared/ComponentPlaceholder";

import FarmyBreadcrumbs from "../../layouts/components/FarmyBreadcrumbs";
import ProductImageBlock from "./ProductImageBlock";
import ProductDetails from "./ProductDetails";
import FavoritesTogglePDP from "./FavoritesTogglePDP";
import ProducerBlockPDP from "./ProducerBlockPDP";
import CartBlockPDP from "./CartBlockPDP";
import DeliveryNotesPDP from "./DeliveryNotesPDP";
import ProductInfo from "./ProductInfo";
import NutritionalInfoPDP from "./NutritionalInfoPDP";
import AllergensPDP from "./AllergensPDP";

import RecommendationsPDP from "./RecommendationsPDP";
import Ingredients from "./Ingredients";
import {Link} from "react-router-dom";

const theme = currentTheme();

const ProductPageDesktop = (props) => {
  const {product} = props;
  
  const {widthOverStep} = useWindowTools();
  const {currentUser} = useSelector(state => state.session);
  const {imagesLoadComplete} = useImageLoadWatch([product?.id]);

  return <PageContentWithCart
    showCart={widthOverStep("xl", true)}
    sidebarClass="margin-top-20"
    className="product-page-desktop"
  >
    <FarmyColumn className="margin-bottom-20">
      <ComponentPlaceholder
        style={{
          minHeight: "20px",
          maxWidth: "50%"
        }}
        until={product?.id}
      >
        <FarmyBreadcrumbs
          breadcrumbs={product?.breadcrumbs}
          className="text-small"
        />
      </ComponentPlaceholder>
    </FarmyColumn>

    <FarmyColumnSet className="gap-20" style={{flexWrap: "auto"}}>
      <FarmyColumn width="500px" className="flex-shrink-0">
        <ComponentPlaceholder
          className="full-width" style={{aspectRatio: "500 / 379"}}
          until={product?.id && imagesLoadComplete}
          hideChildren={false}
          transparent={product?.id}
        >
          <ProductImageBlock product={product}/>
        </ComponentPlaceholder>

        <Title2 className="text-color-green font-weight-600 margin-top-20">
          <ComponentPlaceholder
            style={{minHeight: "26.4px"}}
            until={product?.id}
          >
            {t("product_page.details")}
          </ComponentPlaceholder>
        </Title2>

        <hr style={{
          borderColor: theme.colors.farmy[product?.id ? "lightGrey" : "lightestGrey"]
        }}/>

        <Title3 className="text-strong margin-bottom-10">
          <ComponentPlaceholder
            until={product?.id}
            style={{minHeight: "24.5px"}}
          >
            {product?.name}
          </ComponentPlaceholder>
        </Title3>

        <ComponentPlaceholder
          until={product?.id}
          style={{
            minHeight: "150px"
          }}
        >
          <ProductDetails product={product}/>
        </ComponentPlaceholder>

        <ComponentPlaceholder
          until={product?.id}
          className="margin-top-20"
          style={{
            minHeight: "150px"
          }}
        >
          <Ingredients product={product}/>
        </ComponentPlaceholder>

        <ComponentPlaceholder
          until={product?.id}
          style={{minHeight: "180px"}}
          className="margin-top-20"
        >
          {product?.nutritionalInfo && <>
            <Title3 className="text-strong margin-top-20 margin-bottom-15">
              {product?.nutritionalInfo?.label}
            </Title3>
            <NutritionalInfoPDP nutritionalInfo={product.nutritionalInfo}/>
          </>}
        </ComponentPlaceholder>

        <ComponentPlaceholder
          until={product?.id}
          style={{minHeight: "65px"}}
          className="margin-top-20"
        >
          {!!product?.allergens?.length && <>
            <Title3 className="text-strong margin-top-20 margin-bottom-20">
              {t("farmy_product.allergens.header")}
            </Title3>
            <AllergensPDP allergens={product?.allergens}/>
          </>}
        </ComponentPlaceholder>
      </FarmyColumn>

      <FarmyColumn className="flex-grow-1 padding-right-30">
        <ComponentPlaceholder
          until={product?.id}
          style={{minHeight: "34.5px"}}
          className="full-width margin-top-5 margin-bottom-15"
        >
          <Title1 className={`
            product-title
            flex
            justify-content-space-between
            align-items-baseline
            margin-top-5
            margin-bottom-15
          `}>
            <span>
              {product?.name}

              {/* Admin link */}
              {currentUser?.isAdmin
                ? <Link
                  to={`/admin/products/${product?.id}/edit`}
                  target="_blank"
                  className="text-color-very-light-grey font-size-body margin-left-10"
                >
                  <FaLink/>
                </Link>
                : null
              }
            </span>

            <FavoritesTogglePDP
              product={product}
              setProduct={props?.setProduct}
              size="26px"
              className="margin-top-10"
            />
          </Title1>
        </ComponentPlaceholder>

        <FarmyColumnSet className="gap-20 margin-top-5">
          <FarmyColumn width="260px" className="flex-shrink-0">
            <ComponentPlaceholder
              until={product?.id && imagesLoadComplete}
              style={{minHeight: "390px", zIndex: 1}}
              hideChildren={false}
              transparent={product?.id}
            >
              <ProducerBlockPDP producer={product?.supplier}/>
            </ComponentPlaceholder>
          </FarmyColumn>

          <FarmyColumn width="320px">
            <ComponentPlaceholder
              until={product?.id}
              style={{minHeight: "300px"}}
              className="border-radius-xl"
            >
              <CartBlockPDP product={product} classname="margin-top-20"/>
            </ComponentPlaceholder>

            <ComponentPlaceholder
              until={product?.id}
              style={{minHeight: "78px"}}
              className="margin-top-20 border-radius-xl"
            >
              <DeliveryNotesPDP product={product} className="margin-top-20"/>
            </ComponentPlaceholder>
          </FarmyColumn>
        </FarmyColumnSet>

        <FarmyColumnSet className="margin-top-10">
          <FarmyColumn width="75%">
            <ComponentPlaceholder
              until={product?.id}
              style={{minHeight: "290px"}}
            >
              <ProductInfo product={product}/>
            </ComponentPlaceholder>
          </FarmyColumn>
        </FarmyColumnSet>
      </FarmyColumn>
    </FarmyColumnSet>

    <FarmyColumnSet className="padding-right-20 padding-left-20">
      <FarmyColumn>
        <RecommendationsPDP product={product}/>
      </FarmyColumn>
    </FarmyColumnSet>
  </PageContentWithCart>;
};

export default ProductPageDesktop;
